// Der Ereignisbus zwischen Simulation und Oberflaeche.
//
// `scenes/` und `ui/` liegen auf derselben Schicht und importieren sich
// **nicht** gegenseitig (Gate FG-04). Ohne einen gemeinsamen Kanal muesste
// eine von beiden die andere kennen — und dann zieht die Oberflaeche
// irgendwann Zeichencode mit sich herum.
//
// Bewusst winzig und typisiert: ein Ereignisname, der nirgends deklariert ist,
// faellt beim Typcheck auf statt zur Laufzeit still ins Leere zu gehen.

/**
 * Ein zerstoerter Brocken, so weit ihn die Oberflaeche kennen muss.
 *
 * Bewusst **nicht** der `Rock` aus `sim/`: `core` liegt darunter und darf ihn
 * nicht importieren — auch nicht als Typ. Ein Typ-Import ist zur Laufzeit
 * unsichtbar, aber er dreht die Abhaengigkeitsrichtung im Kopf jedes Lesers um.
 */
export interface DestroyedRock {
  id: string;
  x: number;
  y: number;
  r: number;
  ore: string;
  rich: boolean;
}

export interface BusEvents {
  /** Der Spielzustand hat sich geaendert — HUD und Panels neu bewerten. */
  'state:changed': void;
  /**
   * Kurze Meldung am Bildschirmrand.
   *
   * `bad` ist der Ton einer **Ablehnung**. Er fehlte, und ohne ihn sah ein
   * „das geht nicht" genauso aus wie ein „erledigt": `tone: ''` erzeugt
   * `class="toast "` ganz ohne Akzentfarbe.
   */
  'toast': { text: string; tone?: 'good' | 'gold' | 'bad' | '' };
  /** Ein Panel oeffnen oder schliessen. */
  'panel:toggle': { tab: PanelTab };
  'panel:close': void;
  /**
   * Ob gerade ein Panel offen ist.
   *
   * Die Szene braucht das, weil das Panel den Canvas **nicht** mehr abschirmt:
   * es laesst den Technologiebaum durchscheinen und durchklicken. Ohne diese
   * Meldung wuerde jeder Klick daneben das Schiff losfliegen lassen.
   */
  'panel:state': { open: boolean };
  /** Der Technologiebaum soll sich neu zeichnen. */
  'tree:refresh': void;
  /**
   * Ob gerade ein Zug beim Server ist.
   *
   * Online laeuft **ein** Zug zur Zeit (`net/moves.ts`), und jeder weitere
   * Klick wird solange still verworfen. Fuer einen Fehlgriff ist das richtig;
   * am Ressourcenkonverter drueckt derselbe Mensch **denselben** Knopf
   * zwanzigmal, weil ein Rezept eine Einheit liefert — und sah dort nur, dass
   * nichts passiert. Der Knopf zeigt deshalb, dass er gerade wartet.
   *
   * Solo bleibt es aus: dort laeuft der Zug synchron, es gibt nichts zu
   * ueberbruecken.
   */
  'move:busy': { busy: boolean };
  /** Ein Brocken wurde zerstoert — fuer Ton und Effekte. */
  'rock:destroyed': DestroyedRock;
  /**
   * Ein Erzposten ist im Deck angekommen.
   *
   * Der Kanal zwischen Szene und HUD (`scenes` und `ui` kennen sich nicht,
   * Gate FG-04). Das HUD laesst den passenden Chip kurz aufleuchten — sonst
   * springt dort nur eine Zahl, und niemand sieht, welche.
   */
  'res:gained': { res: string };
  /** Verbindungszustand des Online-Modus. */
  'net:status': { online: boolean; players: number; field: string };
  /**
   * Das Konto hat gewechselt — an- oder abgemeldet.
   *
   * Die Verbindung traegt den Token in ihrer Adresse; sie muss danach neu
   * aufgebaut werden, sonst spielt der Spieler unter dem alten Konto weiter.
   */
  'account:changed': void;
  /** Ein Kolossbrocken erscheint, veraendert sich oder ist erledigt. */
  'colossus': { id: number; hp: number; maxHp: number; done: boolean } | null;
  /**
   * Restdauer der laufenden Notabschaltung in Sekunden; 0 heisst „vorbei".
   *
   * Das HUD zeigt den Ruecklauf, die Szene sperrt die Steuerung. Beide lesen
   * dieselbe Zahl — solo aus der Szenenuhr, online vom Server.
   */
  'tow': { left: number };
  /**
   * Wer gerade auf das Schiff feuert — Anzahl und der naechstgelegene Name.
   *
   * Ohne diese Meldung stuende der Spieler im Wachbereich einer Schwarmmutter
   * (780 Einheiten!), saehe seinen Abbau aussetzen und faende keinen Grund.
   * `count: 0` heisst „kein Gefecht".
   */
  'combat': { count: number; name: string };
  /**
   * Die Heimflug-Sequenz ist vorbei — abgelaufen oder übersprungen.
   *
   * Beide Wege enden hier, damit die Welt genau einmal wieder anläuft. Die
   * Sequenz **entscheidet** damit nichts: die Ära stand schon vorher im
   * Spielstand (`GAME-001`, `VIEW-001`).
   */
  'jump:done': void;
  /**
   * Die Landesequenz (Akt III) ist vorbei — abgelaufen oder übersprungen.
   *
   * Derselbe eine Ausgang wie beim Heimflug: die Kapsel stand schon vorher im
   * Spielstand, die Sequenz zeigt sie nur.
   */
  'landing:done': void;
}

export type PanelTab =
  | 'upgrades' | 'base' | 'quests' | 'map' | 'boosters' | 'points' | 'stats'
  | 'ranking' | 'log' | 'finds' | 'colony' | 'help';

type Handler<K extends keyof BusEvents> = (payload: BusEvents[K]) => void;

class EventBus {
  private readonly handlers = new Map<string, Set<(payload: never) => void>>();
  /** Ereignisse, die gerade zugestellt werden — gegen den Ringschluss. */
  private readonly zustellend = new Set<string>();

  on<K extends keyof BusEvents>(event: K, handler: Handler<K>): () => void {
    const set = this.handlers.get(event) ?? new Set();
    set.add(handler as (payload: never) => void);
    this.handlers.set(event, set);
    return () => set.delete(handler as (payload: never) => void);
  }

  /**
   * Ein Ereignis an alle Zuhoerer zustellen.
   *
   * Meldet ein Zuhoerer dasselbe Ereignis erneut, ist das ein **Ringschluss**
   * und kein Sonderfall: die Zustellung laeuft sonst bis zum Stapelueberlauf,
   * und die Zeilen **nach** dem `emit` werden nie erreicht. Genau daran hing
   * `panel:state` — das Panel schloss sich, aber die Szene erfuhr es nie, und
   * auf dem Telefon liess sich das Schiff danach nicht mehr steuern.
   *
   * Deshalb ein **lauter** Fehler mit dem Namen des Ereignisses statt eines
   * stillen Abbruchs: der Ringschluss ist ein Denkfehler in der Verdrahtung.
   */
  emit<K extends keyof BusEvents>(event: K, payload: BusEvents[K]): void {
    const set = this.handlers.get(event);
    if (!set) return;
    if (this.zustellend.has(event)) {
      throw new Error(`Ringschluss im Ereignisbus: „${event}" meldet sich selbst erneut.`);
    }
    this.zustellend.add(event);
    try {
      for (const handler of set) (handler as Handler<K>)(payload);
    } finally {
      this.zustellend.delete(event);
    }
  }

  /** Nur fuer Tests: alle Zuhoerer entfernen. */
  reset(): void {
    this.handlers.clear();
    this.zustellend.clear();
  }
}

export const bus = new EventBus();
